import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useUpdateCandidateV2, useCompanyUsersV2 } from '@/hooks/useCandidatesV2';

const SOURCES = ['manual', 'linkedin', 'referral', 'imported'];
const SOURCE_LABELS = { manual: 'Manual', linkedin: 'LinkedIn', referral: 'Referral', imported: 'Imported' };

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const toForm = (c) => ({
  firstName: c?.firstName || '',
  lastName: c?.lastName || '',
  email: c?.email || '',
  phone: c?.phone || '',
  location: c?.location || '',
  currentTitle: c?.currentTitle || '',
  currentCompany: c?.currentCompany || '',
  experienceYears: c?.experienceYears != null ? String(c.experienceYears) : '',
  source: c?.source || '',
  owner: c?.owner?._id || c?.owner || '',
  tags: Array.isArray(c?.tags) ? c.tags.join(', ') : '',
  skills: Array.isArray(c?.skills) ? c.skills.join(', ') : '',
  summary: c?.summary || '',
  resumeUrl: c?.resumeUrl || '',
});

const EditCandidateModalV2 = ({ open, candidate, onClose }) => {
  const [form, setForm] = useState(toForm(candidate));
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');

  const { data: usersData } = useCompanyUsersV2();
  const users = Array.isArray(usersData) ? usersData : (usersData?.users || []);
  const updateMutation = useUpdateCandidateV2();

  useEffect(() => {
    if (candidate) { setForm(toForm(candidate)); setErrors({}); setServerError(''); }
  }, [candidate]);

  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setErrors((p) => ({ ...p, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!isValidEmail(form.email)) e.email = 'Invalid email address';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setServerError('');
    if (!validate()) return;

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim() || undefined,
      email: form.email.trim(),
      phone: form.phone.trim() || undefined,
      location: form.location.trim() || undefined,
      currentTitle: form.currentTitle.trim() || undefined,
      currentCompany: form.currentCompany.trim() || undefined,
      experienceYears: form.experienceYears !== '' ? Number(form.experienceYears) : undefined,
      source: form.source || undefined,
      owner: form.owner || undefined,
      tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      skills: form.skills ? form.skills.split(',').map((s) => s.trim()).filter(Boolean) : [],
      summary: form.summary.trim() || undefined,
      resumeUrl: form.resumeUrl.trim() || undefined,
    };

    updateMutation.mutate(
      { id: candidate._id, data: payload },
      {
        onSuccess: () => onClose(),
        onError: (err) => {
          const msg = err.response?.data?.message;
          if (err.response?.status === 400 && msg) setServerError(msg);
        },
      }
    );
  };

  if (!candidate) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Candidate</DialogTitle>
        </DialogHeader>

        {serverError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-3 py-2">{serverError}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>First Name <span className="text-red-500">*</span></Label>
              <Input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
              {errors.firstName && <p className="text-xs text-red-500">{errors.firstName}</p>}
            </div>
            <div className="space-y-1">
              <Label>Last Name</Label>
              <Input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
              {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
            </div>
            <div className="space-y-1">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => set('location', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Current Title</Label>
              <Input value={form.currentTitle} onChange={(e) => set('currentTitle', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Current Company</Label>
              <Input value={form.currentCompany} onChange={(e) => set('currentCompany', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Experience (years)</Label>
              <Input type="number" min="0" max="50" value={form.experienceYears} onChange={(e) => set('experienceYears', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Source</Label>
              <Select value={form.source || 'none'} onValueChange={(v) => set('source', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{SOURCE_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Owner</Label>
              <Select value={form.owner || 'none'} onValueChange={(v) => set('owner', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No owner</SelectItem>
                  {users.map((u) => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Tags <span className="text-xs text-gray-400">(comma-separated)</span></Label>
            <Input value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder="frontend, react, senior" />
          </div>

          <div className="space-y-1">
            <Label>Skills <span className="text-xs text-gray-400">(comma-separated)</span></Label>
            <Input value={form.skills} onChange={(e) => set('skills', e.target.value)} placeholder="JavaScript, TypeScript, Node.js" />
          </div>

          <div className="space-y-1">
            <Label>Summary</Label>
            <Textarea value={form.summary} onChange={(e) => set('summary', e.target.value)} rows={3} />
          </div>

          <div className="space-y-1">
            <Label>Resume URL</Label>
            <Input value={form.resumeUrl} onChange={(e) => set('resumeUrl', e.target.value)} placeholder="https://..." />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCandidateModalV2;
